// scheduling/pages/ClientView.jsx
// Per-client dashboard: jobs with budget bars, hours-by-level breakdown, team roster,
// dependency indicators, prior year history.

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchClients, fetchJobs, fetchSchedule, fetchDependencies,
         fetchJobsHistory, fetchJobsHistoryWIP, fetchJobsWIP, fetchExperienceRanking, fetchStaff } from '../services/schedData';
import { setJobOnSite } from '../services/schedApi';
import { BRAND, JOB_TYPE_COLORS, SCHED_STATUS, computeSchedStatus } from '../utils/schedConstants';
import JobProgressBar from '../components/JobProgressBar';
import ExperienceRankingList from '../components/ExperienceRankingList';
import OnSiteToggle from '../components/OnSiteToggle';
import ScheduleHistoryModal from './ScheduleHistoryModal';
import ClientPYHistogram from '../components/ClientPYHistogram';
import { useUrlState } from '../../hooks/useUrlState';
import { useAuth } from '../../auth/AuthContext';

function fmtDate(iso) {
  if (!iso) return '\u2014';
  return new Date(typeof iso === 'string' ? iso.slice(0, 10) + 'T12:00:00' : iso)
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const GRADE_COLOR = {
  A: { bg: '#003D4C', text: '#fff' },
  B: { bg: '#356371', text: '#fff' },
  C: { bg: '#74A282', text: '#fff' },
};

const LEVEL_GROUPS = [
  { key: 'partner', label: 'Partner', match: /member|principal|partner/i },
  { key: 'manager', label: 'Manager', match: /senior manager|manager|supervisor/i },
  { key: 'senior', label: 'Senior', match: /senior accountant/i },
  { key: 'staff', label: 'Staff', match: /staff|intern|consultant/i },
];

function classifyLevel(level) {
  if (!level) return 'staff';
  for (const g of LEVEL_GROUPS) {
    if (g.match.test(level)) return g.key;
  }
  return 'staff';
}

export default function ClientView() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { username, isSuperUser } = useAuth();
  const [myStaffId, setMyStaffId] = useState(null); // for JM/JP gating of the on-site toggle
  const [client, setClient] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]); // for prior year lookup
  const [schedEntries, setSchedEntries] = useState([]);
  const [jobsWip, setJobsWip] = useState([]); // current WIP (logged hours) for this client's jobs
  const [dependencies, setDependencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedJob, setExpandedJob] = useState(null);
  // ?history=<jobId> opens the Schedule-History modal; now two-way (opening /
  // closing the modal updates the URL). The grid still deep-links in via this param.
  const [historyJobId, setHistoryJobId] = useUrlState('history', null, {
    serialize: v => (v == null ? '' : String(v)),
    deserialize: s => (s ? parseInt(s, 10) : null),
  });
  const [historyJobs, setHistoryJobs] = useState([]);
  const [historyWIP, setHistoryWIP] = useState([]);
  const [expandedHistJob, setExpandedHistJob] = useState(null);
  const [hoveredStaffRange, setHoveredStaffRange] = useState(null); // { FirstDate, LastDate } | null
  // Active tab lives in the URL (?tab=) for shareable / navbot deeplinks.
  const [activeTab, setActiveTab] = useUrlState('tab', 'jobs', { validate: ['jobs', 'team', 'history', 'experience'] }); // jobs | team | history | experience
  // Experience tab — ranked staff list for this client (client dimension), lazy-loaded on first open.
  const [clientExperience, setClientExperience] = useState([]);
  const [expLoading, setExpLoading] = useState(false);
  const [expLoaded, setExpLoaded] = useState(false);
  const [expSearch, setExpSearch] = useState('');

  // Resolve the current user's StaffId (email match) for JM/JP gating of the on-site toggle.
  useEffect(() => {
    let cancelled = false;
    fetchStaff().then(list => {
      if (cancelled || !Array.isArray(list)) return;
      const uLower = (username || '').toLowerCase();
      const me = list.find(s => {
        const email = (s.Email || '').toLowerCase();
        return email === uLower + '@bmss.com' || email === uLower;
      });
      setMyStaffId(me ? me.StaffId : null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [username]);

  const cvCanSetOnSite = (j) => isSuperUser || (myStaffId != null && (j.ManagerId === myStaffId || j.PartnerId === myStaffId));

  const handleSetOnSite = async (jobId, next) => {
    setJobs(prev => prev.map(j => (j.JobId === jobId ? { ...j, IsOnSite: next ? 1 : 0 } : j)));
    try {
      await setJobOnSite({ jobId, isOnSite: next });
    } catch {
      setJobs(prev => prev.map(j => (j.JobId === jobId ? { ...j, IsOnSite: next ? 0 : 1 } : j)));
    }
  };

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      setExpLoaded(false); setClientExperience([]); setExpSearch('');
      const cid = parseInt(clientId);
      try {
        const [clientData, jobData, schedData, depData, histData, wipData, curWipData] = await Promise.all([
          fetchClients({ clientId: cid }),
          fetchJobs({ clientId: cid }),
          fetchSchedule({ clientId: cid }),
          fetchDependencies(null), // get all deps, filter client-side
          fetchJobsHistory({ clientId: cid }),
          fetchJobsHistoryWIP({ clientId: cid }),
          fetchJobsWIP({ clientId: cid }),
        ]);
        if (cancelled) return;
        setClient(Array.isArray(clientData) && clientData.length > 0 ? clientData[0] : null);
        setJobs(Array.isArray(jobData) ? jobData : []);
        setAllJobs(Array.isArray(jobData) ? jobData : []);
        setSchedEntries(Array.isArray(schedData) ? schedData : []);
        setDependencies(Array.isArray(depData) ? depData : []);
        setHistoryJobs(Array.isArray(histData) ? histData : []);
        setHistoryWIP(Array.isArray(wipData) ? wipData : []);
        setJobsWip(Array.isArray(curWipData) ? curWipData : []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [clientId]);

  // Lazy-load the client experience ranking the first time the Experience tab opens.
  // Client dimension is keyed on PEClientIndex (not rprs ClientId); backend falls
  // back to DimensionName matching when the id is null.
  useEffect(() => {
    if (activeTab !== 'experience' || !client || expLoaded) return undefined;
    let cancelled = false;
    setExpLoading(true);
    fetchExperienceRanking('client', client.Name, client.PEClientIndex ?? null)
      .then(data => { if (!cancelled) { setClientExperience(Array.isArray(data) ? data : []); setExpLoaded(true); } })
      .catch(() => { if (!cancelled) setExpLoaded(true); })
      .finally(() => { if (!cancelled) setExpLoading(false); });
    return () => { cancelled = true; };
  }, [activeTab, client, expLoaded]);

  // Current WIP (logged hours) per job — total + by level + by staff
  const wipByJob = useMemo(() => {
    const map = {};
    for (const w of jobsWip) {
      if (!w.JobId) continue;
      if (!map[w.JobId]) map[w.JobId] = { total: 0, byLevel: {}, byStaff: [] };
      map[w.JobId].total += w.TotalHours || 0;
      const levelKey = classifyLevel(w.StaffLevel);
      map[w.JobId].byLevel[levelKey] = (map[w.JobId].byLevel[levelKey] || 0) + (w.TotalHours || 0);
      map[w.JobId].byStaff.push({ staffId: w.StaffId, name: w.StaffName || 'Unknown', initials: w.StaffInitials || '??', level: w.StaffLevel || '', hours: w.TotalHours || 0 });
    }
    return map;
  }, [jobsWip]);

  // Future-scheduled (today onward) per job — drives BOTH the progress bar AND the
  // headline totals / Hours-by-Level / Team. Past scheduled hours are already actuals
  // (WIP); counting them double-counts against budget. (Replaced the all-dates
  // schedByJob, which made the job total + level/team tables overstate scheduling.)
  const futureSchedByJob = useMemo(() => {
    const now = new Date();
    const todayISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const map = {};
    for (const e of schedEntries) {
      const eISO = String(e.EntryDate || '').slice(0, 10);
      if (eISO && eISO < todayISO) continue;
      if (!map[e.JobId]) map[e.JobId] = { total: 0, byLevel: {}, byStaff: {}, staff: {} };
      map[e.JobId].total += e.Hours || 0;

      // by level (Hours-by-Level table)
      const levelKey = classifyLevel(e.StaffLevel);
      map[e.JobId].byLevel[levelKey] = (map[e.JobId].byLevel[levelKey] || 0) + (e.Hours || 0);

      // by staffId (progress-bar popovers)
      const k = e.StaffId;
      if (!map[e.JobId].byStaff[k]) map[e.JobId].byStaff[k] = { staffId: k, name: e.StaffName || 'Unknown', initials: e.StaffInitials || '??', hours: 0 };
      map[e.JobId].byStaff[k].hours += e.Hours || 0;

      // by staff name (Team list)
      const sKey = e.StaffName || `Staff ${e.StaffId}`;
      if (!map[e.JobId].staff[sKey]) {
        map[e.JobId].staff[sKey] = { name: sKey, initials: e.StaffInitials || '??', level: e.StaffLevel || '', levelKey: classifyLevel(e.StaffLevel), hours: 0, staffId: e.StaffId };
      }
      map[e.JobId].staff[sKey].hours += e.Hours || 0;
    }
    return map;
  }, [schedEntries]);

  // Dependencies for this client's jobs
  const jobIds = useMemo(() => new Set(jobs.map(j => j.JobId)), [jobs]);
  const clientDeps = useMemo(() =>
    dependencies.filter(d => jobIds.has(d.PredecessorJobId) || jobIds.has(d.DependentJobId)),
  [dependencies, jobIds]);

  // Enrich jobs
  const enrichedJobs = useMemo(() =>
    jobs.map(j => ({
      ...j,
      // Includes placeholders, matching futureScheduled below — this drives the jobs
      // table's Scheduled / Remaining columns and the client-level total, all of which
      // would otherwise contradict the progress bar sitting directly above them.
      // byLevel/team stay placeholder-free on purpose: those are per-STAFF breakdowns
      // and a placeholder has no staffer yet.
      scheduledHours: Math.round(((futureSchedByJob[j.JobId]?.total || 0) + (j.PlaceholderHours || 0)) * 100) / 100,
      byLevel: futureSchedByJob[j.JobId]?.byLevel || {},
      team: Object.values(futureSchedByJob[j.JobId]?.staff || {}),
      wipTotal: Math.round((wipByJob[j.JobId]?.total || 0) * 100) / 100,
      wipByStaff: (wipByJob[j.JobId]?.byStaff || []).sort((a, b) => b.hours - a.hours),
      wipByLevel: wipByJob[j.JobId]?.byLevel || {},
      // Placeholder hours count as scheduled (schedData queryJobs PlaceholderHours,
      // today-onward) — the calendar space is allotted, only the name is outstanding.
      // Folded into futureScheduled so the bar, the status chip and the remaining-hours
      // column all agree; carried separately so the bar can itemise it in its popover.
      futureScheduled: Math.round(((futureSchedByJob[j.JobId]?.total || 0) + (j.PlaceholderHours || 0)) * 100) / 100,
      placeholderHrs: Math.round((j.PlaceholderHours || 0) * 100) / 100,
      futureSchedByStaff: Object.values(futureSchedByJob[j.JobId]?.byStaff || {}).sort((a, b) => b.hours - a.hours),
      deps: clientDeps.filter(d => d.PredecessorJobId === j.JobId || d.DependentJobId === j.JobId),
    })).sort((a, b) => {
      if (a.Status !== b.Status) {
        if (a.Status === 'In Progress') return -1;
        if (b.Status === 'In Progress') return 1;
      }
      return (a.Deadline || '9999') < (b.Deadline || '9999') ? -1 : 1;
    }),
  [jobs, wipByJob, futureSchedByJob, clientDeps]);

  // Unique team across all jobs
  const allTeam = useMemo(() => {
    const map = {};
    for (const j of enrichedJobs) {
      for (const m of j.team) {
        if (!map[m.staffId]) map[m.staffId] = { ...m, jobs: [] };
        map[m.staffId].jobs.push(j.Name);
        map[m.staffId].hours += m.hours;
      }
    }
    return Object.values(map).sort((a, b) => b.hours - a.hours);
  }, [enrichedJobs]);

  // Stats
  const totalBudget = enrichedJobs.reduce((s, j) => s + (j.BudgetHours || 0), 0);
  const totalScheduled = enrichedJobs.reduce((s, j) => s + j.scheduledHours, 0);
  const totalWip = enrichedJobs.reduce((s, j) => s + (j.wipTotal || 0), 0);
  const totalUsed = totalWip + totalScheduled;

  if (loading) return <div className="sched-home-loading"><div className="sched-home-loading-text">Loading client...</div></div>;
  if (error) return <div className="sched-placeholder"><h2>Error</h2><p style={{ color: '#DC2626' }}>{error}</p></div>;
  if (!client) return <div className="sched-placeholder"><h2>Client Not Found</h2></div>;

  const gradeStyle = GRADE_COLOR[client.Grade] || { bg: '#E5E7EB', text: '#374151' };
  const budgetPctTotal = totalBudget > 0 ? Math.round((totalUsed / totalBudget) * 100) : 0;

  return (
    <div className="sched-cv-root">
      {/* ── Header ── */}
      <div className="sched-cv-header" style={{ background: 'linear-gradient(135deg, #003D4C 0%, #356371 100%)', color: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', width: '100%', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <button className="sched-sug-back" style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', padding: '4px 10px', fontSize: 12 }}
                onClick={() => navigate(-1)}>&larr;</button>
              <h2 className="sched-cv-client-name" style={{ color: '#fff', margin: 0 }}>{client.Name}</h2>
              {client.Grade && (
                <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                  background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)' }}>
                  Grade {client.Grade}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 4 }}>
              <span>{client.Code}</span>
              {client.Industry && <span>{client.Industry}</span>}
              {client.OfficeCode && <span>{client.OfficeCode}</span>}
              {client.PartnerName && <span>Partner: <strong style={{ color: '#fff' }}>{client.PartnerName}</strong></span>}
              {client.ManagerName && <span>Manager: <strong style={{ color: '#fff' }}>{client.ManagerName}</strong></span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {client.Code && (
              <a href={`https://bmss.pehosted.com/PE/Client/Dashboard/${client.Code}`}
                target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 8,
                  background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)',
                  textDecoration: 'none', whiteSpace: 'nowrap' }}>
                &#8599; View in PE
              </a>
            )}
          </div>
        </div>
        {/* Quick stats in header */}
        <div className="sched-cv-header-stats">
          {[
            { val: enrichedJobs.length, label: 'Active Jobs', sub: 'current year', color: '#6CC24A' },
            { val: `${totalBudget.toLocaleString()}h`, label: 'Total Budget', sub: 'all jobs', color: '#fff' },
            {
              label: 'Logged + Scheduled',
              sub: totalBudget > 0 ? `${budgetPctTotal}% of budget` : '',
              node: (
                <>
                  <div className="sched-cv-header-stat-val" style={{ color: '#fff' }}>{Math.round(totalUsed).toLocaleString()}h</div>
                  <div style={{ display: 'flex', gap: 10, fontSize: 11, fontWeight: 700, marginTop: 2 }}>
                    <span style={{ color: '#93C5FD' }}>{Math.round(totalWip).toLocaleString()}h WIP</span>
                    <span style={{ color: '#6CC24A' }}>{Math.round(totalScheduled).toLocaleString()}h sched</span>
                  </div>
                </>
              ),
            },
            { val: allTeam.length, label: 'Team Size', sub: 'unique staff', color: '#6CC24A' },
          ].map(s => (
            <div key={s.label} className="sched-cv-header-stat">
              {s.node || <div className="sched-cv-header-stat-val" style={{ color: s.color }}>{s.val}</div>}
              <div className="sched-cv-header-stat-label" style={{ color: 'rgba(255,255,255,0.85)' }}>{s.label}</div>
              {s.sub && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 1 }}>{s.sub}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="sched-tabs">
        {[['jobs', `Jobs (${enrichedJobs.length})`], ['team', `Team (${allTeam.length})`], ['history', `Prior Year (${historyJobs.length})`], ['experience', clientExperience.length ? `Experience (${clientExperience.length})` : 'Experience']].map(([key, label]) => (
          <button key={key} className={`sched-tab ${activeTab === key ? 'sched-tab--active' : ''}`}
            onClick={() => setActiveTab(key)}>{label}</button>
        ))}
      </div>

      <div className="sched-cv-body">

        {/* ── Jobs Tab ── */}
        {activeTab === 'jobs' && (
          <>
            {/* Client-wide budget summary */}
            {totalBudget > 0 && (() => {
              const wipPctW = Math.min(100, (totalWip / totalBudget) * 100);
              const schedPctW = Math.min(Math.max(0, 100 - wipPctW), (totalScheduled / totalBudget) * 100);
              return (
              <div className="sched-cv-summary-card">
                <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', marginBottom: 6 }}>CLIENT-WIDE HOURS SUMMARY</div>
                <div className="sched-cv-budget-track" style={{ height: 8, marginBottom: 8, display: 'flex', overflow: 'hidden' }}>
                  <div style={{ width: `${wipPctW}%`, height: 8, background: BRAND.oceanBlue }} />
                  <div style={{ width: `${schedPctW}%`, height: 8, background: BRAND.sageGreen }} />
                </div>
                <div style={{ display: 'flex', gap: 24, fontSize: 12, flexWrap: 'wrap' }}>
                  <div><span style={{ color: '#9CA3AF' }}>Budget:</span> <strong>{totalBudget}h</strong></div>
                  <div><span style={{ color: '#9CA3AF' }}>Logged:</span> <strong style={{ color: BRAND.oceanBlue }}>{Math.round(totalWip)}h</strong></div>
                  <div><span style={{ color: '#9CA3AF' }}>Scheduled:</span> <strong style={{ color: BRAND.sageGreen }}>{Math.round(totalScheduled)}h</strong></div>
                  <div><span style={{ color: '#9CA3AF' }}>Remaining:</span> <strong>{Math.max(0, totalBudget - Math.round(totalUsed))}h</strong></div>
                </div>
              </div>
              );
            })()}

            {enrichedJobs.length === 0 && <div className="sched-cv-empty">No jobs found for this client.</div>}

            {enrichedJobs.map(j => {
              const typeColor = JOB_TYPE_COLORS[j.ServiceDesc] || JOB_TYPE_COLORS[j.JobType] || { bg: '#E5E7EB', text: '#374151' };
              const isExpanded = expandedJob === j.JobId;
              const used = Math.round((j.wipTotal + j.futureScheduled) * 100) / 100;
              const budgetPct = j.BudgetHours > 0 ? Math.round((used / j.BudgetHours) * 100) : 0;

              return (
                <div key={j.JobId} className={`sched-cv-job${isExpanded ? ' sched-cv-job--expanded' : ''}`}
                  onClick={() => setExpandedJob(isExpanded ? null : j.JobId)}>

                  {/* Left accent bar */}
                  <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: typeColor.bg, borderRadius: '10px 0 0 10px' }} />

                  {/* Job header */}
                  <div className="sched-cv-job-header" style={{ paddingLeft: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span className="sched-type-tag" style={{ background: typeColor.bg, color: typeColor.text }}>
                          {j.ServiceDesc || j.JobType || 'Other'}
                        </span>
                        <span className="sched-cv-job-status" style={{
                          color: j.Status === 'In Progress' ? '#1D4ED8' : j.Status === 'Not Started' ? '#6B7280' : '#065F46',
                        }}>
                          {j.StatusDetail || j.Status || 'Not Started'}
                        </span>
                        {j.deps.length > 0 && <span title="Has dependencies" style={{ fontSize: 14 }}>&#128279;</span>}
                      </div>
                      <div className="sched-cv-job-name">{j.Name}</div>
                      {(j.IsOnSite || cvCanSetOnSite(j)) && (
                        <div style={{ margin: '3px 0' }} onClick={(e) => e.stopPropagation()}>
                          <OnSiteToggle isOnSite={!!j.IsOnSite} office={j.Office} officeCode={j.OfficeCode}
                            canEdit={cvCanSetOnSite(j)} onChange={(next) => handleSetOnSite(j.JobId, next)} />
                        </div>
                      )}
                      <div className="sched-cv-job-meta">
                        {j.Code && <>{j.Code} &middot; </>}
                        Due: {fmtDate(j.Deadline)}
                        {j.PartnerName && <> &middot; {j.PartnerName}</>}
                        {j.ManagerName && <> &middot; {j.ManagerName}</>}
                      </div>
                    </div>

                    <div className="sched-cv-job-hours">
                      <div className="sched-cv-job-hours-main">{used}h</div>
                      <div className="sched-cv-job-hours-label">of {j.BudgetHours || 0}h ({budgetPct}%)</div>
                    </div>
                  </div>

                  {/* Budget bar */}
                  <div className="sched-cv-job-bar" style={{ marginLeft: 12 }}>
                    <div className="sched-cv-budget-track" style={{ height: 6 }}>
                      <div className="sched-cv-budget-fill" style={{
                        width: `${Math.min(budgetPct, 100)}%`, height: 6,
                        background: budgetPct > 100 ? '#EF4444' : budgetPct > 80 ? '#F59E0B' : BRAND.oceanBlue,
                        borderRadius: 3,
                      }} />
                    </div>
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="sched-cv-job-details" style={{ marginLeft: 12 }}>

                      {/* Job View item 4 — progress: WIP (logged) + future-scheduled vs budget */}
                      {(() => {
                        const budget = j.BudgetHours || 0;
                        const used = Math.round((j.wipTotal + j.futureScheduled) * 100) / 100;
                        const remaining = Math.max(0, Math.round((budget - used) * 100) / 100);
                        const overBudget = used > budget;
                        const overage = overBudget ? Math.round((used - budget) * 100) / 100 : 0;
                        const pctComplete = budget > 0 ? Math.round((used / budget) * 100) : 0;
                        const st = SCHED_STATUS[computeSchedStatus(budget, j.futureScheduled, j.wipTotal)];
                        return (
                          <div style={{ marginBottom: 14 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                              <div className="sched-cv-section-title" style={{ margin: 0 }}>Progress</div>
                              {st && <span className="sched-grid-job-chip" style={{ marginLeft: 'auto', background: st.bg, color: st.text }}><span className="sched-grid-job-chip-dot" style={{ background: st.dot }} />{st.label}</span>}
                            </div>
                            <JobProgressBar budget={budget} wipTotal={j.wipTotal} scheduledTotal={j.futureScheduled} placeholderHours={j.placeholderHrs}
                              remaining={remaining} overBudget={overBudget} overage={overage} pctComplete={pctComplete}
                              wipByStaff={j.wipByStaff} scheduledByStaff={j.futureSchedByStaff} />
                          </div>
                        );
                      })()}

                      {/* Hours by level table \u2014 WIP logged + scheduled (today-on) */}
                      <div className="sched-cv-section-title">Hours by Level</div>
                      <table className="sched-cv-level-table">
                        <thead>
                          <tr>
                            <th>Level</th>
                            <th style={{ textAlign: 'right' }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}><span style={{ width: 8, height: 8, borderRadius: 2, background: BRAND.oceanBlue }} />WIP Logged</span></th>
                            <th style={{ textAlign: 'right' }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}><span style={{ width: 8, height: 8, borderRadius: 2, background: BRAND.sageGreen }} />Scheduled</span></th>
                            <th style={{ textAlign: 'right' }}>Budget</th>
                            <th style={{ textAlign: 'right' }}>Remaining</th>
                          </tr>
                        </thead>
                        <tbody>
                          {LEVEL_GROUPS.map(lg => {
                            const wip = Math.round((j.wipByLevel[lg.key] || 0) * 100) / 100;
                            const sched = Math.round((j.byLevel[lg.key] || 0) * 100) / 100;
                            const budget = j[`Budget${lg.label}`] || 0;
                            const remaining = Math.round((budget - wip - sched) * 100) / 100;
                            return (
                              <tr key={lg.key}>
                                <td>{lg.label}</td>
                                <td style={{ textAlign: 'right', fontWeight: 600 }}>{wip}h</td>
                                <td style={{ textAlign: 'right', fontWeight: 600 }}>{sched}h</td>
                                <td style={{ textAlign: 'right' }}>{budget > 0 ? `${budget}h` : '\u2014'}</td>
                                <td style={{ textAlign: 'right', color: remaining < 0 ? '#EF4444' : '#6B7280', fontWeight: remaining < 0 ? 700 : 400 }}>
                                  {budget > 0 ? (remaining < 0 ? `+${Math.abs(remaining)}h over` : `${remaining}h`) : '\u2014'}
                                </td>
                              </tr>
                            );
                          })}
                          <tr style={{ fontWeight: 700, borderTop: '2px solid #E5E7EB' }}>
                            <td>Total</td>
                            <td style={{ textAlign: 'right' }}>{j.wipTotal}h</td>
                            <td style={{ textAlign: 'right' }}>{j.scheduledHours}h</td>
                            <td style={{ textAlign: 'right' }}>{j.BudgetHours || 0}h</td>
                            <td style={{ textAlign: 'right', color: j.BudgetHours && (j.wipTotal + j.scheduledHours) > j.BudgetHours ? '#EF4444' : '#374151' }}>
                              {j.BudgetHours ? `${Math.max(0, Math.round((j.BudgetHours - j.wipTotal - j.scheduledHours) * 100) / 100)}h` : '\u2014'}
                            </td>
                          </tr>
                        </tbody>
                      </table>

                      {/* Dependencies */}
                      {j.deps.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <div className="sched-cv-section-title">Dependencies</div>
                          {j.deps.map(d => (
                            <div key={d.DependencyId} className="sched-cv-dep-card">
                              <span>&#128279;</span>
                              <div>
                                <div style={{ fontSize: 12, fontWeight: 600 }}>
                                  {d.PredecessorJobId === j.JobId
                                    ? `${d.DependentJobName} depends on this job`
                                    : `This job depends on ${d.PredecessorJobName}`}
                                </div>
                                {d.Notes && <div style={{ fontSize: 11, color: '#6B7280' }}>{d.Notes}</div>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Team roster — WIP logged + scheduled-on per member (union of both) */}
                      {(() => {
                        const byId = {};
                        for (const m of j.team) {
                          byId[m.staffId] = { staffId: m.staffId, name: m.name, initials: m.initials, level: m.level, wipHours: 0, schedHours: m.hours };
                        }
                        for (const w of j.wipByStaff) {
                          if (!byId[w.staffId]) byId[w.staffId] = { staffId: w.staffId, name: w.name, initials: w.initials, level: w.level || '', wipHours: 0, schedHours: 0 };
                          byId[w.staffId].wipHours = w.hours;
                          if (!byId[w.staffId].level && w.level) byId[w.staffId].level = w.level;
                        }
                        const members = Object.values(byId).sort((a, b) => (b.wipHours + b.schedHours) - (a.wipHours + a.schedHours));
                        return (
                          <>
                            <div className="sched-cv-section-title" style={{ marginTop: 12 }}>Team ({members.length})</div>
                            {members.length === 0 ? (
                              <div style={{ fontSize: 12, color: '#9CA3AF' }}>No staff logged or scheduled</div>
                            ) : (
                              <div className="sched-cv-team-list">
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 6px 2px' }}>
                                  <div style={{ width: 26 }} />
                                  <div style={{ flex: 1 }} />
                                  <div style={{ width: 56, textAlign: 'right', fontSize: 10, fontWeight: 700, color: BRAND.oceanBlue }}>WIP</div>
                                  <div style={{ width: 56, textAlign: 'right', fontSize: 10, fontWeight: 700, color: BRAND.sageGreen }}>SCHED</div>
                                </div>
                                {members.map((m, i) => (
                                  <div key={i} className="sched-cv-team-member"
                                    onClick={(e) => { e.stopPropagation(); navigate(`/schedule/employee/${m.staffId}`); }}>
                                    <div className="sched-cv-team-avatar" style={{ background: BRAND.oceanBlue }}>
                                      {m.initials}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <div className="sched-cv-team-name">{m.name}</div>
                                      <div className="sched-cv-team-level">{m.level}</div>
                                    </div>
                                    <div className="sched-cv-team-hours" style={{ width: 56, textAlign: 'right', color: m.wipHours ? BRAND.oceanBlue : '#D1D5DB' }}>{Math.round(m.wipHours * 100) / 100}h</div>
                                    <div className="sched-cv-team-hours" style={{ width: 56, textAlign: 'right', color: m.schedHours ? BRAND.sageGreen : '#D1D5DB' }}>{Math.round(m.schedHours * 100) / 100}h</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        );
                      })()}

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                        <button className="sched-assign-btn" style={{ flex: 1 }}
                          onClick={(e) => { e.stopPropagation(); navigate(`/schedule/grid?view=jobs&job=${j.JobId}`); }}>
                          View in Scheduler
                        </button>
                        <button className="sched-grid-action-btn" style={{ flex: 1 }}
                          onClick={(e) => { e.stopPropagation(); setHistoryJobId(j.JobId); }}>
                          &#128203; View Job History
                        </button>
                        {j.PEJobIndex && (
                          <button className="sched-grid-action-btn sched-grid-action-pe" style={{ flex: 1 }}
                            onClick={(e) => { e.stopPropagation(); window.open(`https://bmss.pehosted.com/PE/Job/Job/${j.PEJobIndex}?panel=0`, '_blank'); }}>
                            &#8599; View in PE
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}

        {/* ── Team Tab ── */}
        {activeTab === 'team' && (
          <div>
            <div className="sched-cv-section-title" style={{ padding: '0 0 8px' }}>
              All Team Members Across {enrichedJobs.length} Jobs
            </div>
            {allTeam.length === 0 ? (
              <div className="sched-cv-empty">No staff scheduled for any jobs.</div>
            ) : (
              <div className="sched-cv-team-list">
                {allTeam.map(m => (
                  <div key={m.staffId} className="sched-cv-team-member"
                    onClick={() => navigate(`/schedule/employee/${m.staffId}`)}
                    style={{ cursor: 'pointer' }}>
                    <div className="sched-cv-team-avatar" style={{ background: BRAND.oceanBlue }}>
                      {m.initials}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="sched-cv-team-name">{m.name}</div>
                      <div className="sched-cv-team-level">{m.level} &middot; {m.jobs.length} job{m.jobs.length !== 1 ? 's' : ''}</div>
                    </div>
                    <div className="sched-cv-team-hours">{Math.round(m.hours * 100) / 100}h</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* ── Prior Year History Tab ── */}
        {activeTab === 'history' && (() => {
          // Build WIP lookup by PEJobIndex
          const wipByJob = {};
          for (const w of historyWIP) {
            if (!wipByJob[w.PEJobIndex]) wipByJob[w.PEJobIndex] = [];
            wipByJob[w.PEJobIndex].push(w);
          }

          // Summary stats — prefer audit-derived EffectiveCompletedDate; fall back to PE Job_Finish_Actual
          const jobCompletion = (j) => j.CompletedDate || j.DueActual;
          const jobsWithDates = historyJobs.filter(j => j.Deadline && jobCompletion(j));
          const onTimeJobs = jobsWithDates.filter(j => jobCompletion(j).slice(0, 10) <= j.Deadline.slice(0, 10));
          const onTimePct = jobsWithDates.length > 0 ? Math.round((onTimeJobs.length / jobsWithDates.length) * 100) : 0;
          const jobsWithActual = historyJobs.filter(j => j.ActualHours != null && j.ActualHours > 0 && j.BudgetHours > 0);
          const avgVariance = jobsWithActual.length > 0
            ? Math.round(jobsWithActual.reduce((s, j) => s + (j.ActualHours - j.BudgetHours), 0) / jobsWithActual.length * 100) / 100
            : 0;
          const totalPYHours = historyJobs.reduce((s, j) => s + (j.ActualHours || 0), 0);

          return (
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: BRAND.deepTeal, margin: '0 0 4px' }}>
                Prior Year Completion History
              </h3>
              <p style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 16 }}>
                Historical completion dates, budget vs. actual hours, and prior year teams. Use this data when rolling forward schedules.
              </p>

              {historyJobs.length === 0 ? (
                <div className="sched-cv-empty">No historical jobs found for this client. Run a PE sync with the pe_jobs_hist Logic App case to populate.</div>
              ) : (
                <>
                  {/* History table — teal header matching DJB mockup */}
                  <div style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: BRAND.deepTeal }}>
                          {['Job', 'Internal Due Date', 'Completed', 'Budget Hrs', 'Actual Hrs', 'Variance', 'Team'].map(h => (
                            <th key={h} style={{ padding: '10px 12px', textAlign: h === 'Job' || h === 'Team' ? 'left' : 'center',
                              color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: 0.3 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {historyJobs.map((j, i) => {
                          const typeColor = JOB_TYPE_COLORS[j.ServiceDesc] || JOB_TYPE_COLORS[j.JobType] || { bg: '#E5E7EB', text: '#374151' };
                          const completedDate = j.CompletedDate || j.DueActual;
                          const onTime = completedDate && j.Deadline && completedDate.slice(0, 10) <= j.Deadline.slice(0, 10);
                          const variance = j.ActualHours != null && j.ActualHours > 0 && j.BudgetHours > 0
                            ? (j.ActualHours - j.BudgetHours) : null;
                          const varianceColor = variance == null ? '#9CA3AF' : variance > 5 ? '#DC2626' : variance > 0 ? '#B45309' : '#065F46';
                          const team = wipByJob[j.PEJobIndex] || [];
                          const isExpanded = expandedHistJob === j.HistJobId;

                          return (
                            <React.Fragment key={j.HistJobId}>
                            <tr style={{ background: i % 2 === 1 ? '#FAFAFA' : '#fff', cursor: 'pointer', borderBottom: '1px solid #F3F4F6' }}
                              onClick={() => setExpandedHistJob(isExpanded ? null : j.HistJobId)}>
                              <td style={{ padding: '10px 12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span className="sched-type-tag" style={{ background: typeColor.bg, color: typeColor.text, fontSize: 10 }}>
                                    {j.ServiceDesc || j.JobType || 'Other'}
                                  </span>
                                  <span style={{ fontWeight: 600, fontSize: 13, color: BRAND.deepTeal }}>{j.Name}</span>
                                </div>
                                {/* Expanded: team detail + job info */}
                                {isExpanded && (
                                  <div style={{ marginTop: 10 }}>
                                    {j.PeriodEnd && <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 6 }}>Period: {fmtDate(j.PeriodStart)} &ndash; {fmtDate(j.PeriodEnd)}</div>}
                                    {team.length > 0 ? (
                                      <div style={{ background: '#F9FAFB', borderRadius: 8, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
                                        <div style={{ padding: '8px 10px', background: '#F3F4F6', fontSize: 11, fontWeight: 700, color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>
                                          TEAM DETAIL ({team.length} staff)
                                        </div>
                                        {team.map((w, wi) => (
                                          <div key={wi}
                                            onMouseEnter={() => { if (w.FirstDate && w.LastDate) setHoveredStaffRange({ FirstDate: w.FirstDate, LastDate: w.LastDate }); }}
                                            onMouseLeave={() => setHoveredStaffRange(null)}
                                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12,
                                            padding: '6px 10px', borderBottom: wi < team.length - 1 ? '1px solid #F3F4F6' : 'none',
                                            background: hoveredStaffRange && hoveredStaffRange.FirstDate === w.FirstDate && hoveredStaffRange.LastDate === w.LastDate ? '#FEF3C7' : 'transparent',
                                            cursor: 'default' }}>
                                            <div>
                                              <span style={{ fontWeight: 600, color: w.StaffIsActive === false ? '#D97706' : '#111827' }}>
                                                {w.StaffIsActive === false && '\u26A0 '}{w.StaffName || `Staff #${w.PEStaffIndex}`}
                                              </span>
                                              <span style={{ color: '#9CA3AF', fontWeight: 400, marginLeft: 6 }}>{w.StaffLevel || ''}</span>
                                            </div>
                                            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                              <span style={{ fontWeight: 600, color: '#374151' }}>{Number(w.TotalHours || 0).toFixed(2)}h</span>
                                              {w.FirstDate && w.LastDate && (
                                                <span style={{ color: '#9CA3AF', fontSize: 11 }}>
                                                  {fmtDate(w.FirstDate).replace(/, \d{4}/, '')} &ndash; {fmtDate(w.LastDate).replace(/, \d{4}/, '')}
                                                  {w.WeekCount && ` (${w.WeekCount}wk)`}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>No WIP detail available for this job.</div>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: '10px 12px', textAlign: 'center', fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>{fmtDate(j.Deadline)}</td>
                              <td style={{ padding: '10px 12px', textAlign: 'center', fontSize: 12, whiteSpace: 'nowrap' }}>
                                {completedDate ? (
                                  <span style={{ color: onTime ? '#065F46' : '#DC2626' }}
                                    title={j.EffectiveCompletedDate ? 'From PE audit log (status \u2192 Completed/Closed)' : 'From PE Job_Finish_Actual field'}>
                                    {onTime ? '\u2713 ' : '\u26A0 '}{fmtDate(completedDate)}
                                  </span>
                                ) : <span style={{ color: '#9CA3AF' }}>&mdash;</span>}
                              </td>
                              <td style={{ padding: '10px 12px', textAlign: 'center', fontSize: 12, color: '#444' }}>{j.BudgetHours > 0 ? Number(j.BudgetHours).toFixed(2) : '\u2014'}</td>
                              <td style={{ padding: '10px 12px', textAlign: 'center', fontSize: 12, color: '#444' }}>{j.ActualHours != null && j.ActualHours > 0 ? Number(j.ActualHours).toFixed(2) : 'N/A'}</td>
                              <td style={{ padding: '10px 12px', textAlign: 'center', fontSize: 12, fontWeight: 600, color: varianceColor }}>
                                {variance != null ? (variance > 0 ? `+${variance.toFixed(2)}` : variance.toFixed(2)) : '\u2014'}
                              </td>
                              <td style={{ padding: '10px 12px', fontSize: 12, color: '#666' }}>
                                {team.length > 0
                                  ? team.slice(0, 3).map(w => w.StaffName || `Staff #${w.PEStaffIndex}`).join(', ')
                                      + (team.length > 3 ? ` + ${team.length - 3} more` : '')
                                  : (j.PartnerName || j.ManagerName || '\u2014')}
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr key={`${j.HistJobId}-hist`} style={{ background: i % 2 === 1 ? '#FAFAFA' : '#fff', borderBottom: '1px solid #F3F4F6' }}>
                                <td colSpan={7} style={{ padding: 0 }}>
                                  <div style={{ borderTop: '1px solid #E5E7EB', padding: '10px 16px', background: '#FAFAFA' }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', marginBottom: 6, letterSpacing: 0.3 }}>
                                      PY WORK DISTRIBUTION
                                    </div>
                                    <ClientPYHistogram
                                      peJobIndex={j.PEJobIndex}
                                      deadline={j.Deadline}
                                      completionDate={completedDate}
                                      highlightStart={hoveredStaffRange?.FirstDate}
                                      highlightEnd={hoveredStaffRange?.LastDate}
                                    />
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Summary stats — matching DJB mockup card style */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginTop: 20 }}>
                    <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 16, textAlign: 'center' }}>
                      <div style={{ fontSize: 24, fontWeight: 700, color: '#6CC24A' }}>{onTimeJobs.length} / {jobsWithDates.length}</div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: BRAND.deepTeal, marginTop: 4 }}>On-Time Completion</div>
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{onTimePct}% on time (last 3 years)</div>
                    </div>
                    <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 16, textAlign: 'center' }}>
                      <div style={{ fontSize: 24, fontWeight: 700, color: avgVariance > 5 ? '#DC2626' : avgVariance > 0 ? '#B45309' : '#065F46' }}>
                        {avgVariance > 0 ? '+' : ''}{avgVariance} hrs
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: BRAND.deepTeal, marginTop: 4 }}>Avg Budget Variance</div>
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>Average overage per engagement</div>
                    </div>
                    <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 16, textAlign: 'center' }}>
                      <div style={{ fontSize: 24, fontWeight: 700, color: BRAND.oceanBlue }}>{Math.round(totalPYHours).toLocaleString()}</div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: BRAND.deepTeal, marginTop: 4 }}>Total Actual Hours</div>
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>Across {historyJobs.length} historical jobs</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* ── Experience Tab ── */}
        {activeTab === 'experience' && (
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: BRAND.deepTeal, margin: '0 0 4px' }}>
              Client Experience Ranking
            </h3>
            <p style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 12 }}>
              Staff ranked by experience on <strong>{client?.Name}</strong> (logged-hours percentile) &mdash; handy for staffing decisions and understanding why someone is or isn&rsquo;t recommended.
            </p>
            <input
              type="text"
              placeholder="Search staff..."
              value={expSearch}
              onChange={e => setExpSearch(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 12, marginBottom: 8, boxSizing: 'border-box' }}
            />
            <ExperienceRankingList
              rows={clientExperience.filter(r => !expSearch || (r.Name || '').toLowerCase().includes(expSearch.toLowerCase()))}
              loading={expLoading}
              onStaffClick={(sid) => navigate(`/schedule/employee/${sid}`)}
            />
          </div>
        )}
      </div>

      {/* Schedule History Modal */}
      {historyJobId && (() => {
        const hJob = enrichedJobs.find(j => j.JobId === historyJobId);
        return (
          <ScheduleHistoryModal
            jobId={historyJobId}
            jobName={hJob?.Name || 'Unknown'}
            clientName={client?.Name || ''}
            onClose={() => setHistoryJobId(null)}
          />
        );
      })()}
    </div>
  );
}
